import React, { useMemo } from 'react';
import { Html, useCursor } from '@react-three/drei';
import type { EquipmentStatus } from './types';
import { AMHS_BODY_SHADING, AMHS_PALETTE, STATUS_LED_COLOR } from './types';

/** Small status LED — does not recolor the whole machine. */
export const StatusIndicator: React.FC<{
  status?: EquipmentStatus;
  position?: [number, number, number];
  size?: number;
}> = ({ status = 'IDLE', position = [0, 0, 0], size = 0.08 }) => {
  const color = STATUS_LED_COLOR[status];
  const intensity =
    status === 'OFFLINE'
      ? 0.1
      : status === 'RUNNING'
        ? 1.25
        : status === 'IDLE'
          ? 0.95
          : 0.85;
  return (
    <mesh position={position}>
      <sphereGeometry args={[size, 12, 12]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={intensity}
        metalness={0.1}
        roughness={0.35}
      />
    </mesh>
  );
};

export const AmhsLabel: React.FC<{
  text: string;
  y?: number;
  /** Full local-space anchor. Overrides `y` so a label can sit on a machine face. */
  position?: [number, number, number];
  visible?: boolean;
}> = ({ text, y = 0.35, position, visible = true }) => {
  if (!visible || !text) return null;
  return (
    <Html
      position={position ?? [0, y, 0]}
      center
      distanceFactor={18}
      style={{
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        padding: '2px 8px',
        borderRadius: 4,
        background: 'rgba(15,23,42,0.78)',
        color: '#f8fafc',
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        border: '1px solid rgba(148,163,184,0.35)',
      }}
    >
      {text}
    </Html>
  );
};

/** Shared interactive wrapper for hover cursor / click without a ground ring. */
export const AmhsInteractiveGroup: React.FC<{
  children: React.ReactNode;
  selected?: boolean;
  onSelect?: () => void;
  name?: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number | [number, number, number];
  /** @deprecated Unused — ground highlight ring removed. */
  highlightRadius?: number;
}> = ({ children, onSelect, name, position, rotation, scale }) => {
  const [hovered, setHovered] = React.useState(false);
  useCursor(hovered);

  return (
    <group
      name={name}
      position={position}
      rotation={rotation}
      scale={scale}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={() => setHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.();
      }}
    >
      {children}
    </group>
  );
};

export function useIndustrialMaterials(opacity = 1) {
  return useMemo(
    () => ({
      stocker: {
        color: AMHS_PALETTE.stockerFrame,
        ...AMHS_BODY_SHADING,
        transparent: opacity < 1,
        opacity,
      },
      stockerDark: {
        color: AMHS_PALETTE.stockerFrameDark,
        ...AMHS_BODY_SHADING,
        transparent: opacity < 1,
        opacity,
      },
      metal: {
        color: AMHS_PALETTE.metal,
        metalness: 0.7,
        roughness: 0.32,
        transparent: opacity < 1,
        opacity,
      },
      body: {
        color: AMHS_PALETTE.body,
        metalness: 0.25,
        roughness: 0.45,
        transparent: opacity < 1,
        opacity,
      },
      blue: {
        color: AMHS_PALETTE.amhsBlue,
        metalness: 0.35,
        roughness: 0.4,
        transparent: opacity < 1,
        opacity,
      },
    }),
    [opacity]
  );
}
